import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft, CaretRight, Ticket } from '@phosphor-icons/react';
import {
  API,
  authConfig,
  alertError,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
  thStyle,
  tdStyle,
  pageTitleStyle,
  downloadTicketsCsvExport,
  printReportAsPdf,
  ticketClientBranchLabel,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import TicketDomainBadge from '../components/TicketDomainBadge';
import ListSearch, { filterBySearch } from '../../../components/ListSearch';
import { TicketDetailLink } from '../ticketNav';
import { StatusDonut, CategoryPie, VolumeBar, SkeletonBlock, EmptyState } from '../../../components/module-ui';

const PAGE_SIZE = 20;
const TICKET_SEARCH_KEYS = [
  'ticket_no', 'subject', 'status', 'priority', 'domain', 'category',
  'assignee_names', 'created_by_name', 'reporter.name',
];

function TicketReportsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(monthAgo);
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dateParams = () => ({
    date_from: `${fromDate}T00:00:00.000Z`,
    date_to: `${toDate}T23:59:59.999Z`,
  });

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = dateParams();
      const [summaryRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/reports/summary`, authConfig(params)),
        axios.get(`${API}/reports/tickets`, authConfig(params)),
      ]);
      setSummary(summaryRes.data);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
      setPage(1);
      setSearch('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report');
      setSummary(null);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const filteredTickets = useMemo(
    () => filterBySearch(tickets, search, TICKET_SEARCH_KEYS),
    [tickets, search],
  );

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageTickets = filteredTickets.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const from = filteredTickets.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(safePage * PAGE_SIZE, filteredTickets.length);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleExportCsv = async () => {
    try {
      await downloadTicketsCsvExport(dateParams());
    } catch (err) {
      alert(err.response?.data?.detail || 'CSV export failed');
    }
  };

  const handlePrintPdf = () => {
    const byStatus = summary?.by_status || {};
    printReportAsPdf('Ticket Report', [
      { label: 'Date range', value: `${fromDate} to ${toDate}` },
      { label: 'Total tickets', value: summary?.total ?? tickets.length },
      { label: 'Open', value: summary?.open ?? byStatus.open ?? 0 },
      { label: 'In Progress', value: summary?.in_progress ?? byStatus.in_progress ?? 0 },
      { label: 'Completed', value: summary?.completed ?? byStatus.completed ?? 0 },
      { label: 'Closed', value: summary?.closed ?? byStatus.closed ?? 0 },
    ], document.getElementById('ticket-report'));
  };

  const volumeByDay = useMemo(() => {
    const map = {};
    tickets.forEach((t) => {
      const d = (t.created_at || '').slice(0, 10);
      if (!d) return;
      if (!map[d]) map[d] = { label: d.slice(5), created: 0, closed: 0 };
      map[d].created += 1;
      if (['closed', 'completed'].includes(t.status)) map[d].closed += 1;
    });
    return Object.keys(map).sort().map((k) => map[k]);
  }, [tickets]);

  const priorityPie = useMemo(() => {
    const map = {};
    tickets.forEach((t) => {
      const p = t.priority || 'unknown';
      map[p] = (map[p] || 0) + 1;
    });
    return map;
  }, [tickets]);

  const slaBar = useMemo(() => {
    const atRisk = summary?.sla_at_risk ?? 0;
    const breached = summary?.sla_breached ?? 0;
    const ok = Math.max((summary?.total ?? tickets.length) - atRisk - breached, 0);
    return [
      { label: 'On track', created: ok },
      { label: 'At risk', created: atRisk },
      { label: 'Breached', created: breached },
    ];
  }, [summary, tickets]);

  const statusData = summary?.by_status || {
    open: summary?.open,
    in_progress: summary?.in_progress,
    completed: summary?.completed,
    closed: summary?.closed,
    pending: summary?.pending,
  };

  const statCard = (label, value, color = '#002FA7') => (
    <div style={{ padding: '10px', background: '#F9FAFB', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
      <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '12px', fontWeight: '700', color }}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div id="ticket-report">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
        <div>
          <h2 style={pageTitleStyle}>Ticket Reports</h2>
          <Link to="/dashboard/tickets/reports/member" style={{ fontSize: '12px', color: '#002FA7' }}>
            Member ticket-wise report →
          </Link>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end', marginBottom: '10px' }}>
        <div>
          <label style={labelStyle}>From Date</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>To Date</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={fieldStyle} />
        </div>
        <button type="button" onClick={loadReport} disabled={loading} style={btnPrimary}>
          {loading ? 'Loading…' : 'Apply'}
        </button>
        <button type="button" onClick={handleExportCsv} style={btnSecondary}>Export CSV</button>
        <button type="button" onClick={handlePrintPdf} style={btnSecondary}>Print / PDF</button>
      </div>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      {loading && !summary ? <SkeletonBlock rows={6} /> : null}

      {summary && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '10px' }}>
            {statCard('Total', summary.total ?? summary.total_tickets)}
            {statCard('Open', summary.open ?? summary.by_status?.open ?? 0, '#F59E0B')}
            {statCard('In Progress', summary.in_progress ?? summary.by_status?.in_progress ?? 0, '#3B82F6')}
            {statCard('Completed', summary.completed ?? summary.by_status?.completed ?? 0, '#10B981')}
            {statCard('Closed', summary.closed ?? summary.by_status?.closed ?? 0, '#6B7280')}
            {statCard('Pending', summary.pending ?? summary.by_status?.pending ?? 0, '#8B5CF6')}
          </div>

          <div className="wd-chart-grid">
            <StatusDonut data={statusData} title="Status mix" />
            <CategoryPie data={priorityPie} title="Priority mix" />
            <VolumeBar data={volumeByDay} title="Daily volume" />
            <VolumeBar data={slaBar} title="SLA risk" createdKey="created" />
          </div>
        </>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Tickets in Range</h3>
        <div style={{ flex: '1 1 240px', maxWidth: '420px' }}>
          <ListSearch
            value={search}
            onChange={handleSearchChange}
            placeholder="Search ticket no, subject, status, assignee…"
            resultCount={pageTickets.length}
            totalCount={filteredTickets.length}
            maxWidth="420px"
            style={{ marginBottom: 0 }}
          />
        </div>
      </div>

      {filteredTickets.length === 0 && !loading ? (
        <EmptyState
          icon={Ticket}
          title={tickets.length === 0 ? 'No tickets in selected range' : 'No tickets match your search'}
          description="Adjust the date range or clear search."
        />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={thStyle}>Ticket No</th>
                  <th style={thStyle}>Domain</th>
                  <th style={thStyle}>Client / Branch</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Priority</th>
                  <th style={thStyle}>Created (IST)</th>
                </tr>
              </thead>
              <tbody>
                {pageTickets.map((t) => (
                  <tr key={t.ticket_no} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={tdStyle}>
                      <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>
                        {t.ticket_no}
                      </TicketDetailLink>
                    </td>
                    <td style={tdStyle}><TicketDomainBadge domain={t.domain} /></td>
                    <td style={tdStyle}>{ticketClientBranchLabel(t)}</td>
                    <td style={tdStyle}>{t.subject}</td>
                    <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                    <td style={tdStyle}>{t.priority}</td>
                    <td style={tdStyle}>{formatUtcToIst(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tk-pagination" role="navigation" aria-label="Tickets in range pagination">
            <span className="tk-pagination-meta">
              {from}–{to} of {filteredTickets.length} · Page {safePage} of {totalPages}
            </span>
            <div className="tk-pagination-actions">
              <button
                type="button"
                className="tk-btn tk-btn-secondary"
                disabled={safePage <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <CaretLeft size={14} weight="bold" />
                Previous
              </button>
              <button
                type="button"
                className="tk-btn tk-btn-secondary"
                disabled={safePage >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TicketReportsPage;
